import "core-js/modules/es.array.find-index.js";
import { useRef } from 'react';

/**
 * A React hook helper for implementing the WAI-ARIA 'Feed' pattern.
 * See https://www.w3.org/TR/wai-aria-practices-1.1/#feed for reference.
 *
 * Spread the result of the returned prop getter functions onto the wrapper
 * and item elements of the feed to apply the needed props.
 *
 * @param {object} options
 * @param {bool} options.isLoading - Indicate whether new items are being added to the feed
 * @param {bool} options.totalItemCount - Provide the total number of available items in the feed
 * @returns {object} An object containing the prop getter functions `{getWrapperProps, getItemProps}`
 */

function useAriaFeedProps() {
  var options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
  var isLoading = options.isLoading,
    _options$totalItemCou = options.totalItemCount,
    totalItemCount = _options$totalItemCou === void 0 ? -1 : _options$totalItemCou;
  var feedItemsRef = useRef([]);
  function registerItemRef(index) {
    return function (element) {
      feedItemsRef.current[index] = element;
    };
  }
  function getFocusedItemIndex() {
    var focusedItem = document.activeElement;
    var index = feedItemsRef.current.findIndex(function (element) {
      return element === focusedItem || element.contains(focusedItem);
    });
    return index;
  }
  function moveFocusToItem(index) {
    if (feedItemsRef.current[index]) {
      feedItemsRef.current[index].focus();
    }
  }
  function moveFocusBy(offset) {
    var currentIndex = getFocusedItemIndex();
    if (currentIndex === -1) return;
    moveFocusToItem(currentIndex + offset);
  }
  function focusFirstItem() {
    moveFocusToItem(0);
  }
  function focusLastItem() {
    moveFocusToItem(feedItemsRef.current.length - 1);
  }
  function handleKeyPress(event) {
    switch (event.key) {
      case 'PageDown':
        event.preventDefault();
        moveFocusBy(1);
        break;
      case 'PageUp':
        event.preventDefault();
        moveFocusBy(-1);
        break;
      case 'Home':
        if (event.ctrlKey) {
          event.preventDefault();
          focusFirstItem();
        }
        break;
      case 'End':
        if (event.ctrlKey) {
          event.preventDefault();
          focusLastItem();
        }
        break;
      default:
    }
  }
  var getWrapperProps = function getWrapperProps(_ref) {
    var labelledBy = _ref.labelledBy;
    return {
      role: 'feed',
      'aria-labelledby': labelledBy,
      'aria-busy': isLoading,
      onKeyDown: handleKeyPress
    };
  };
  var getItemProps = function getItemProps(_ref2) {
    var index = _ref2.index,
      labelledBy = _ref2.labelledBy,
      describedBy = _ref2.describedBy;
    return {
      role: 'article',
      'aria-posinset': index + 1,
      'aria-setsize': totalItemCount,
      'aria-labelledby': labelledBy,
      'aria-describedby': describedBy,
      tabIndex: -1,
      ref: registerItemRef(index)
    };
  };
  return {
    getWrapperProps: getWrapperProps,
    getItemProps: getItemProps
  };
}
export default useAriaFeedProps;